/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */
import { useCallback, useEffect, useMemo, Children, useState, forwardRef } from 'react';
import { FormattedMessage } from 'react-intl-next';
import { cssMap, jsx } from '@atlaskit/css';
import { ErrorMessage, Field, RequiredAsterisk } from '@atlaskit/form';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { B100, N0, N20, N40, R400 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import UserPicker, {
	type ExternalUser,
	type Group,
	type Team,
	type OptionData,
	type UserPickerProps,
	type Value,
	isEmail,
	isExternalUser,
} from '@atlaskit/user-picker';
import { fg } from '@atlaskit/platform-feature-flags';

import { useCoreInvitesJiraProjectContext } from '../../experiments/coreInvitesInJiraProjects/CoreInvitesJiraProjectContext';
import { USER_PICKER_ID, type InviteeListId } from '../../analytics';
import { useThirdPartyState, ThirdPartyConnectedTo, UpdateConnection } from '../ThirdParty';
import { mergeAndSearchExternalUsers } from '../ThirdParty/utils';
import { useInvitePeopleContext } from '../InvitePeople';
import { messages } from '../i18n';
import { ScopeState, ZIndex } from '../../types';
import { extractDomainFromEmail, removeZeroWidthCharacters } from '../../utils';
import { useAnalytics } from '../../analytics';
import { useViralSettingsContext } from '../ViralSettings';

import { useDebounce } from './useDebounce';
import {
	determineValidationState,
	validateEmail,
	type ValidationState,
	hasError,
} from './validate';
import { InviteeListAnalytics } from './InviteeListAnalytics';
import { useInviteeListStateContext, getEmailAndIdArrays } from './InviteeListContext';
import type { UserPickerRef } from '@atlaskit/user-picker/types';
import { getTargetingGate } from '../../feature-gate';

type LoadOptions = (searchText: string | undefined) => Promise<OptionData[]>;

const styles = cssMap({
	fieldWrapper: {
		position: 'relative',
	},
	label: {
		display: 'block',
		font: token(
			'font.heading.xxsmall',
			'normal 600 12px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Ubuntu, system-ui, "Helvetica Neue", sans-serif',
		),
		fontWeight: token('font.weight.semibold', '600'),
		color: token('color.text.subtlest', '#626F86'),
		marginTop: token('space.0', '0px'),
		marginBottom: token('space.050', '4px'),
	},
});

export interface OwnProps {
	restrictedDomains?: string[];
	maxEmails?: number;
	onInviteeListChange?: (emails: string[], validEmails: string[]) => void;
	showEmptyError?: boolean;
	autoFocus?: boolean;
}

const UserPickerError = ({
	validationList,
	isEmptyInput,
	maxEmails,
}: {
	validationList: ValidationState[];
	isEmptyInput: boolean;
	maxEmails: number;
}) => {
	const message = getTargetingGate('groot-invite-people-emessage-consistency')
		? messages.inviteeListPickerNoteAlt
		: messages.inviteeListPickerNote;
	return (
		<div data-testid="testId-invite-people-invitee-list-error-messages">
			{Children.toArray(
				validationList.map(
					(validationState) =>
						validationState.validationState === 'error' &&
						validationState.errorMessage && (
							<ErrorMessage>
								<FormattedMessage {...validationState.errorMessage} values={{ maxEmails }} />
							</ErrorMessage>
						),
				),
			)}
			{isEmptyInput && (
				<ErrorMessage>
					<FormattedMessage {...message} />
				</ErrorMessage>
			)}
		</div>
	);
};

const InviteeList = forwardRef<UserPickerRef, OwnProps>(
	(
		{ maxEmails = 10, onInviteeListChange = () => {}, showEmptyError = false, autoFocus = false },
		ref,
	) => {
		const [inputValue, setInputValue] = useState<string | undefined>(undefined);
		const analytics = useAnalytics(InviteeListAnalytics);
		const { isCoreInvitesInJiraProjectEnabled } = useCoreInvitesJiraProjectContext();
		const {
			allowEmail,
			setUserPickerOptions,
			userPickerOptions,
			experimental_userPickerByline,
			inviteeListLabelText,
			inviteeListPlaceholderText,
			inviteeListRequired = true,
			groupByTypeOrder,
		} = useInviteeListStateContext();
		const { onInviteeListFocus } = useViralSettingsContext();

		const {
			queryThirdParty,
			integrations,
			updateIntegrations,
			isConnected: thirdPartyConnected,
		} = useThirdPartyState();
		const { invitePromptOnLinkV3Cohort, inviteeProvider, loggedInEmailDomain, userEmail } =
			useInvitePeopleContext();

		const [hasValidationError, validationList] = useMemo(() => {
			const { emails, ids } = getEmailAndIdArrays(userPickerOptions);
			const validationList = determineValidationState(
				emails,
				ids,
				maxEmails,
				userEmail,
				isCoreInvitesInJiraProjectEnabled,
			);
			return [hasError(validationList), validationList];
		}, [maxEmails, userPickerOptions, userEmail, isCoreInvitesInJiraProjectEnabled]);

		// This is used to make the "Enter up to 10 email addresses" an error if
		// the field is empty; EXCEPT it won't show until the form has been submitted
		const isEmptyInput = userPickerOptions.length === 0 && showEmptyError;

		const onFocusEventWithAnalytics = useCallback(
			(pickerId: InviteeListId) => () => {
				analytics.inviteeListFocused(pickerId);
				onInviteeListFocus();
			},
			[analytics, onInviteeListFocus],
		);
		const userPickerTypedDebounce = useDebounce(2000);

		const getNewEmail = (prevEmails: string[], emails: string[]): string => {
			const newEmails = emails.filter((email: string) => !prevEmails.includes(email));
			return newEmails.shift() || '';
		};

		const onUserSelectionChange = useCallback(
			(value: Value) => {
				let values: OptionData[] = [];
				if (Array.isArray(value)) {
					values = value;
				} else if (value) {
					values = [value];
				}

				for (const value of values) {
					if (isEmail(value)) {
						value.name = removeZeroWidthCharacters(value.name);
						value.id = removeZeroWidthCharacters(value.id);
					}
					if (isExternalUser(value) && value.sources.includes('slack')) {
						analytics.slackUserSelected();
					}
				}

				const { emails } = getEmailAndIdArrays(values);

				setUserPickerOptions((prevEmails) => {
					// Only fire analytics event if an email is added
					const { emails: previousEmailsStrings } = getEmailAndIdArrays(prevEmails);
					prevEmails.length < values.length &&
						analytics.inviteeListItemAdded(USER_PICKER_ID, {
							domains: [extractDomainFromEmail(getNewEmail(previousEmailsStrings, emails)) || ''],
						});
					return values;
				});
			},
			[setUserPickerOptions, analytics],
		);

		// Validator and call onInviteeListChange
		useEffect(() => {
			const { emails, ids } = getEmailAndIdArrays(userPickerOptions);

			if (emails.length === 0 && ids.length === 0) {
				onInviteeListChange([], []);
				return;
			}
			const selectOptions = [...emails, ...ids];
			// Finally, send back all entered email addresses to the parent component
			onInviteeListChange(
				selectOptions,
				// valid emails
				emails.filter((email) => validateEmail(email) === 'VALID'),
			);
		}, [userPickerOptions, maxEmails, onInviteeListChange, userEmail]);

		const loadOptionsBase = useCallback<LoadOptions>(
			async (searchText) => {
				if (!searchText || searchText.trim().length === 0) {
					return [];
				}

				const onInviteeLoadOptionsPromise =
					inviteeProvider?.onInviteeLoadOptions({ searchText }) ?? Promise.resolve([]);
				const queryThirdPartyPromise = queryThirdParty(searchText);

				const [thirdPartySearch, inviteeLoadOptionsSearch] = await Promise.all([
					queryThirdPartyPromise,
					onInviteeLoadOptionsPromise,
				]);

				const providedAdditionalUsers: ExternalUser[] = [];
				const groups: Group[] = [];
				const teams: Team[] = [];

				inviteeLoadOptionsSearch.forEach((item) => {
					if (item.type === 'group') {
						groups.push(item);
					} else if (item.type === 'team') {
						teams.push(item);
					} else {
						providedAdditionalUsers.push(item);
					}
				});

				let mergedExternalUsers: ExternalUser[] = thirdPartySearch;
				if (providedAdditionalUsers.length > 0) {
					mergedExternalUsers = mergeAndSearchExternalUsers({
						providedAdditionalUsers,
						searchText,
						connectedThirdPartyUsers: thirdPartySearch,
					});
				}

				const options = fg('support_teams_in_jira_project_role')
					? [...groups, ...teams, ...mergedExternalUsers]
					: [...groups, ...mergedExternalUsers];
				const { onInviteeResultsMerged } = inviteeProvider || {};

				if (onInviteeResultsMerged) {
					return onInviteeResultsMerged(options);
				}

				return options;
			},
			[inviteeProvider, queryThirdParty],
		);

		const withTypedEvent = useCallback(
			(loadOptions: LoadOptions): LoadOptions =>
				async (searchText) => {
					const options = await loadOptions(searchText);
					if (searchText !== undefined) {
						userPickerTypedDebounce(() => {
							analytics.userPickerTyped({
								queryLength: searchText.length,
								results: options.map((option) => ({
									type: option.type ?? null,
									// Note that sources from cache will only be added if option
									// is loaded from org-people-picker
									sources: (option as ExternalUser).sources,
								})),
							});
						});
					}
					return options;
				},
			[analytics, userPickerTypedDebounce],
		);

		const loadOptions = useMemo(
			() => withTypedEvent(loadOptionsBase),
			[loadOptionsBase, withTypedEvent],
		);

		const customStyles = useMemo(() => {
			return {
				control: (css: any, state: any) => {
					const borderColor =
						hasValidationError || isEmptyInput
							? token('color.border.danger', R400)
							: state.isFocused
								? token('color.border.focused', B100)
								: token('color.border', N40);

					return {
						...css,
						borderColor,
						backgroundColor: state.isFocused
							? token('color.background.input.hovered', N20)
							: token('color.background.input', N0),
						':hover': {
							...css[':hover'],
							backgroundColor: token('color.background.input.hovered', N20),
							borderColor,
						},
					};
				},
				menuPortal: (provided: any) => ({ ...provided, zIndex: ZIndex.FLOATING }),
			};
		}, [isEmptyInput, hasValidationError]);

		const commonUserPickerProps = useMemo(() => {
			const onInputChange = (query?: string) => {
				const trimmedValue = query?.trimStart();
				setInputValue(trimmedValue);
			};
			const pickerPlaceholder = <FormattedMessage {...messages.inviteeListUserPickerPlaceholder} />;
			const placeholder = inviteeListPlaceholderText || pickerPlaceholder;

			const props: Omit<
				UserPickerProps,
				'fieldId' | 'inputId' | 'ariaLabelledBy' | 'onFocus' | 'loadOptions'
			> = {
				ariaLive: 'polite',
				autoFocus,
				onChange: onUserSelectionChange,
				noOptionsMessage: () => null,
				placeholder,
				width: '100%',
				value: userPickerOptions,
				isValidEmail: validateEmail,
				isClearable: true,
				isMulti: true,
				allowEmail,
				suggestEmailsForDomain: loggedInEmailDomain,
				styles: customStyles,
				menuPortalTarget: document.body,
				menuPosition: 'absolute',
				onInputChange,
				search: inputValue,
				required: inviteeListRequired,
				groupByTypeOrder,
			};
			return props;
		}, [
			inviteeListPlaceholderText,
			autoFocus,
			onUserSelectionChange,
			userPickerOptions,
			allowEmail,
			loggedInEmailDomain,
			customStyles,
			inputValue,
			inviteeListRequired,
			groupByTypeOrder,
		]);

		const shouldLoadOptions = !!inviteeProvider?.onInviteeLoadOptions || integrations.size > 0;

		const renderPicker = useCallback(
			(describedByFieldId?: string) => {
				return (
					<>
						<UserPicker
							fieldId="invite-user-picker"
							inputId="invite-user-picker"
							aria-describedby={describedByFieldId}
							aria-labelledby="invite-user-picker-label"
							onFocus={onFocusEventWithAnalytics(USER_PICKER_ID)}
							loadOptions={shouldLoadOptions ? loadOptions : undefined}
							forwardedRef={ref}
							{...commonUserPickerProps}
						/>
						{!!invitePromptOnLinkV3Cohort && (
							<Text as="span" size="small" color="color.text.subtlest">
								{experimental_userPickerByline}
							</Text>
						)}
					</>
				);
			},
			[
				onFocusEventWithAnalytics,
				shouldLoadOptions,
				loadOptions,
				commonUserPickerProps,
				invitePromptOnLinkV3Cohort,
				experimental_userPickerByline,
				ref,
			],
		);

		return (
			<Field name="invite-people-invitee-list">
				{(formProps) => (
					<Box testId="testId-invite-people-invitee-list" xcss={styles.fieldWrapper}>
						<Box
							as="label"
							htmlFor={'invite-user-picker'}
							id={'invite-user-picker-label'}
							xcss={styles.label}
						>
							{inviteeListLabelText || (
								<FormattedMessage {...messages.inviteeListLabelThirdParty} />
							)}
							{inviteeListRequired && <RequiredAsterisk />}
						</Box>
						{renderPicker(`${formProps.fieldProps.id}-error`)}
						<UserPickerError
							validationList={validationList}
							isEmptyInput={isEmptyInput}
							maxEmails={maxEmails}
						/>

						{thirdPartyConnected && <ThirdPartyConnectedTo />}

						{!!updateIntegrations.partialScope.size && (
							<UpdateConnection scope={ScopeState.PARTIAL_SCOPE} />
						)}

						{thirdPartyConnected && !!updateIntegrations.noScope.size && (
							<UpdateConnection scope={ScopeState.NO_SCOPE} />
						)}
					</Box>
				)}
			</Field>
		);
	},
);

export default InviteeList;
